import React from 'react';
import { Collapse, Card, List, Button, InputNumber, Tag, Typography, Space, Empty } from 'antd';
import { PlusOutlined, MinusOutlined, StarFilled, FireOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { useSales } from '../../SalesManager/SalesContext';
import { Product, SaleData, ProductSection } from '../../SalesManager/types';

const { Panel } = Collapse;
const { Text } = Typography;

interface ProductSelectionStepProps {
  data: Partial<SaleData>;
  updateData: (data: Partial<SaleData>) => void;
  adjustedCommissions?: { [businessArea: string]: any };
  currentInstalados?: number;
  currentInstaladosSeguranca?: number;
  segurancaProductsSold?: number;
}

const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({ data, updateData, adjustedCommissions = {}, currentInstalados = 0, currentInstaladosSeguranca = 0, segurancaProductsSold = 0 }) => {
  const { products, sections } = useSales();

  // Create a map of product_id to adjusted commission for quick lookup
  const adjustedCommissionMap = React.useMemo(() => {
    const map = new Map<number, number>();
    // Process Telecomunicação
    if (adjustedCommissions.telecomunicacao?.products) {
      adjustedCommissions.telecomunicacao.products.forEach((p: any) => {
        if (p.has_adjustment && p.adjusted_commission !== p.base_commission) {
          map.set(p.product_id, p.adjusted_commission);
        }
      });
    }
    // Process Segurança
    if (adjustedCommissions.seguranca?.products) {
      adjustedCommissions.seguranca.products.forEach((p: any) => {
        if (p.has_adjustment && p.adjusted_commission !== p.base_commission) {
          map.set(p.product_id, p.adjusted_commission);
        }
      });
    }
    return map;
  }, [adjustedCommissions]);

  // Get commission value for a product (adjusted if available for Telecomunicação or Segurança)
  const getProductCommission = (product: Product): number => {
    const section = sections.find(s => s.id === product.section_id);
    if ((section?.business_area === 'telecomunicacao' || section?.business_area === 'seguranca') && adjustedCommissionMap.has(product.id)) {
      return adjustedCommissionMap.get(product.id)!;
    }
    return product.commission || 0;
  };

  // Helper to get products for a section
  const getProductsForSection = (sectionId: number) => {
    const sectionProducts = products
      .filter(p => p.section_id === sectionId && p.is_active);
    
    // Sort by commission (lowest to highest), but keep highlighted products first
    return sectionProducts.sort((a, b) => {
      // Highlighted products first
      if (a.highlighted !== b.highlighted) {
        return a.highlighted ? -1 : 1;
      }
      // Then sort by commission (lowest to highest)
      const aCommission = getProductCommission(a);
      const bCommission = getProductCommission(b);
      return aCommission - bCommission;
    });
  };

  // Helper to get formatted quantity
  const getQuantity = (productId: number) => {
    const selected = data.selectedProducts?.find(Item => Item.product.id === productId);
    return selected ? selected.quantity : 0;
  };

  const handleUpdateQuantity = (product: Product, quantity: number) => {
    const currentSelected = [...(data.selectedProducts || [])];
    const index = currentSelected.findIndex(item => item.product.id === product.id);

    // Create product with adjusted commission if available
    const productWithCommission = {
      ...product,
      commission: getProductCommission(product)
    };

    if (quantity > 0) {
      if (index >= 0) {
        currentSelected[index] = { product: productWithCommission, quantity };
      } else {
        currentSelected.push({ product: productWithCommission, quantity });
      }
    } else {
      if (index >= 0) {
        currentSelected.splice(index, 1);
      }
    }

    updateData({ selectedProducts: currentSelected });
  };

  // Helper to get max commission for a section (using adjusted if available)
  const getMaxCommissionForSection = (sectionId: number) => {
    const sectionProducts = getProductsForSection(sectionId);
    if (sectionProducts.length === 0) return 0;
    return Math.max(...sectionProducts.map(p => getProductCommission(p)));
  };

  const renderSection = (section: ProductSection) => {
    const sectionProducts = getProductsForSection(section.id);
    if (sectionProducts.length === 0) return null;
    const maxCommission = getMaxCommissionForSection(section.id);

    return (
      <Card
        key={section.id}
        type="inner"
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{section.name}</span>
            {maxCommission > 0 && (
              <span style={{ color: '#10b981', fontWeight: 600, fontSize: 13 }}>
                Até {maxCommission} Pontos
              </span>
            )}
          </div>
        }
        size="small"
        style={{ marginBottom: 16 }}
      >
        <List
          dataSource={sectionProducts}
          renderItem={product => {
            const qty = getQuantity(product.id);
            const isSelected = qty > 0;

            return (
              <List.Item
                style={{
                  backgroundColor: isSelected ? '#e6f7ff' : 'white',
                  transition: 'background-color 0.3s'
                }}
              >
                <List.Item.Meta
                  avatar={product.highlighted && <StarFilled style={{ color: '#faad14' }} />}
                  title={
                    <Space>
                      <Text strong>{product.name}</Text>
                      {product.highlighted && <Tag color="gold">Destaque</Tag>}
                    </Space>
                  }
                  description={
                    <span className="product-commission">
                      <span className="commission-label">Pontos:</span>
                      <Space size="small">
                        {(() => {
                          const section = sections.find(s => s.id === product.section_id);
                          const adjustedComm = getProductCommission(product);
                          const baseComm = product.commission || 0;
                          const isTelecom = section?.business_area === 'telecomunicacao';
                          const isSeguranca = section?.business_area === 'seguranca';
                          const hasAdjustment = (isTelecom || isSeguranca) && adjustedComm !== baseComm;
                          
                          if (hasAdjustment) {
                            return (
                              <>
                                <span style={{ 
                                  textDecoration: 'line-through', 
                                  color: '#999',
                                  fontSize: 13
                                }}>
                                  {baseComm}
                                </span>
                                <span style={{ 
                                  color: '#ff4d4f', 
                                  fontWeight: 700,
                                  fontSize: 15
                                }}>
                                  {adjustedComm}
                                </span>
                              </>
                            );
                          }
                          return <span>{adjustedComm}</span>;
                        })()}
                      </Space>
                    </span>
                  }
                />
                <div className="quantity-controls">
                  <Button
                    shape="circle"
                    icon={<MinusOutlined />}
                    disabled={qty === 0}
                    onClick={() => handleUpdateQuantity(product, qty - 1)}
                    size="small"
                  />
                  <InputNumber
                    min={0}
                    value={qty}
                    onChange={val => handleUpdateQuantity(product, val || 0)}
                    controls={false}
                    className="quantity-input"
                  />
                  <Button
                    shape="circle"
                    icon={<PlusOutlined />}
                    type={qty === 0 ? 'default' : 'primary'}
                    onClick={() => handleUpdateQuantity(product, qty + 1)}
                    size="small"
                  />
                </div>
              </List.Item>
            );
          }}
        />
      </Card>
    );
  };

  // Helper to get max commission for an entire business area (using adjusted if available)
  const getMaxCommissionForArea = (area: 'telecomunicacao' | 'energia' | 'seguranca') => {
    const areaProducts = products.filter(p => {
      const section = sections.find(s => s.id === p.section_id);
      return section?.business_area === area && p.is_active;
    });
    if (areaProducts.length === 0) return 0;
    return Math.max(...areaProducts.map(p => getProductCommission(p)));
  };

  // Group sections by area
  const renderArea = (area: 'telecomunicacao' | 'energia' | 'seguranca', title: string) => {
    const filteredSections = sections.filter(s => s.business_area === area && s.is_active);
    
    // Sort by max commission (points) in descending order
    const areaSections = [...filteredSections].sort((a, b) => {
      const aProducts = getProductsForSection(a.id);
      const bProducts = getProductsForSection(b.id);
      
      const aMaxCommission = aProducts.length > 0
        ? Math.max(...aProducts.map(p => getProductCommission(p)))
        : 0;
      
      const bMaxCommission = bProducts.length > 0
        ? Math.max(...bProducts.map(p => getProductCommission(p)))
        : 0;
      
      return bMaxCommission - aMaxCommission; // Descending order (highest first)
    });

    if (areaSections.length === 0) return <Empty description="Sem produtos disponíveis" />;

    return (
      <div style={{ padding: '16px 0' }}>
        {areaSections.map(renderSection)}
      </div>
    );
  };

  // Render panel header with max points
  const renderPanelHeader = (title: string, area: 'telecomunicacao' | 'energia' | 'seguranca') => {
    const maxCommission = getMaxCommissionForArea(area);
    return (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', paddingRight: 8 }}>
        <span>{title}</span>
        {maxCommission > 0 && (
          <span style={{ color: '#10b981', fontWeight: 600, fontSize: 13 }}>
            Até {maxCommission} Pontos
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="product-selection-step">
      <div className="venda-step-header">
        <h4>Selecione os Produtos</h4>
        <p>Escolha os produtos para esta venda</p>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {adjustedCommissions?.telecomunicacao?.active_breakpoint && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(238, 90, 111, 0.3)'
          }}>
            <FireOutlined style={{ fontSize: 20, color: '#fff' }} />
            <Text strong style={{ color: '#fff', fontSize: 16 }}>
              Produtos de Telecomunicação Instalados pela Intra: <span style={{ fontSize: 20, fontWeight: 700 }}>{currentInstalados}</span>
            </Text>
          </div>
        )}
        {adjustedCommissions?.seguranca?.active_breakpoint && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(238, 90, 111, 0.3)'
          }}>
            <SafetyCertificateOutlined style={{ fontSize: 20, color: '#fff' }} />
            <Text strong style={{ color: '#fff', fontSize: 16 }}>
              Produtos Segurança Instalados por ti: <span style={{ fontSize: 20, fontWeight: 700 }}>{segurancaProductsSold}</span>
            </Text>
          </div>
        )}
      </div>

      <Collapse defaultActiveKey={[]}>
        <Panel header={renderPanelHeader('Telecomunicação', 'telecomunicacao')} key="telecomunicacao">
          {renderArea('telecomunicacao', 'Telecomunicação')}
        </Panel>
        <Panel header={renderPanelHeader('Energia / Solar', 'energia')} key="energia">
          {renderArea('energia', 'Energia')}
        </Panel>
        <Panel header={renderPanelHeader('Segurança', 'seguranca')} key="seguranca">
          {renderArea('seguranca', 'Segurança')}
        </Panel>
      </Collapse>
    </div>
  );
};

export default ProductSelectionStep;


